import { createTheme, type ThemeOptions } from "@mui/material/styles";
import { Z } from "../../../theme/zIndex";
import { getOverlayContainer } from "../../../theme/containerHelper";

type ThemeMode = "light" | "dark";

const colorModeTransition =
  "background-color 180ms ease, color 180ms ease, border-color 180ms ease";
const lightWarning = "#ff9800";
const darkWarning = "#ffb74d";
const darkActionBlue = "#6aa9ff";

const zIndex = {
  appBar: Z.appBar,
  drawer: Z.drawer,
  popover: Z.popover,
  datepicker: Z.datepicker,
  modal: Z.modal,
  snackbar: Z.snackbar,
  tooltip: Z.tooltip,
};

const typography: ThemeOptions["typography"] = {
  fontFamily: `'Inter', 'Arial', sans-serif`,
  h1: { fontFamily: `'Poppins', 'Inter', sans-serif`, fontWeight: 700 },
  h2: { fontFamily: `'Poppins', 'Inter', sans-serif`, fontWeight: 700 },
  h3: { fontFamily: `'Poppins', 'Inter', sans-serif`, fontWeight: 600 },
  h4: { fontFamily: `'Poppins', 'Inter', sans-serif`, fontWeight: 600 },
  h5: { fontFamily: `'Poppins', 'Inter', sans-serif`, fontWeight: 600 },
  h6: { fontFamily: `'Poppins', 'Inter', sans-serif`, fontWeight: 600 },
  button: { textTransform: "none", fontWeight: 500 },
};

const overlayComponents = {
  defaultProps: {
    container: getOverlayContainer,
  },
};

const createComponents = (mode: ThemeMode): ThemeOptions["components"] => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: mode === "dark" ? "#171a1d" : "#f5f5f5",
      },
      "@media (prefers-reduced-motion: no-preference)": {
        body: {
          transition: colorModeTransition,
        },
        "#root, .MuiAppBar-root, .MuiDrawer-paper, .MuiPaper-root, .MuiTableCell-root, .MuiInputBase-root, .MuiChip-root":
          {
            transition: colorModeTransition,
          },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: mode === "dark" ? "#2e2e2e" : "#1f2933",
        color: "#fff",
        fontSize: "0.8rem",
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor:
          mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: "none",
        fontWeight: 500,
        transition: `${colorModeTransition}, transform 180ms ease, box-shadow 180ms ease`,
        "&:hover": {
          transform: "scale(1.03)",
          boxShadow:
            mode === "dark"
              ? "0 4px 20px rgba(0, 0, 0, 0.24)"
              : "0 4px 20px rgba(0, 0, 0, 0.1)",
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        transition: `${colorModeTransition}, box-shadow 180ms ease`,
        ...(mode === "dark" ? { backgroundImage: "none" } : {}),
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        transition: `${colorModeTransition}, transform 180ms ease, box-shadow 180ms ease`,
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow:
            mode === "dark"
              ? "0 8px 24px rgba(0, 0, 0, 0.28)"
              : "0 8px 24px rgba(0, 0, 0, 0.1)",
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: "outlined",
      size: "small",
    },
  },
  MuiDialog: {
    ...overlayComponents,
    styleOverrides: {
      paper: {
        borderRadius: 4,
      },
    },
  },
  MuiDrawer: {
    defaultProps: {
      ModalProps: {
        container: getOverlayContainer,
      },
      transitionDuration: 300,
    },
    styleOverrides: {
      paper: {
        borderRadius: "4px 0 0 4px",
        transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      },
    },
  },
  MuiPopover: {
    ...overlayComponents,
    styleOverrides: {
      paper: {
        zIndex: Z.popover,
      },
    },
  },
  MuiMenu: {
    ...overlayComponents,
    styleOverrides: {
      paper: {
        zIndex: Z.popover,
      },
    },
  },
  MuiPopper: {
    ...overlayComponents,
    styleOverrides: {
      root: {
        zIndex: Z.popover,
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      MenuProps: {
        container: getOverlayContainer,
      },
    },
    styleOverrides: {
      select: {
        "& .MuiSelect-select": {
          zIndex: Z.popover,
        },
      },
    },
  },
});

const createAppTheme = (
  mode: ThemeMode,
  palette: ThemeOptions["palette"]
) =>
  createTheme({
    zIndex,
    palette,
    typography,
    shape: { borderRadius: 4 },
    components: createComponents(mode),
  });

export const themeLight = createAppTheme("light", {
  mode: "light",
  primary: {
    main: "#1976d2",
    light: "#42a5f5",
    dark: "#1565c0",
  },
  secondary: {
    main: "#008c91",
    light: "#33ccd0",
    dark: "#00696b",
  },
  background: {
    default: "#f5f5f5",
    paper: "#ffffff",
  },
  text: {
    primary: "#212121",
    secondary: "#757575",
  },
  divider: "rgba(0,0,0,0.12)",
  info: { main: "#2196f3" },
  success: { main: "#4caf50" },
  warning: { main: lightWarning },
  error: { main: "#f44336" },
  zonas: {
    centro: {
      border: "#99cc00",
      light: "#eaf7d1",
      text: "#5a7a00",
    },
    loma: {
      border: "#00bfc4",
      light: "#e0f7fa",
      text: "#00696b",
    },
    estacionamiento: {
      border: lightWarning,
      light: "#fff3e0",
      text: "#b87000",
    },
    interno: {
      border: "#64748b",
      light: "#e2e8f0",
      text: "#334155",
    },
  },
  especial: {
    background: "#e53935",
    text: "#fff",
  },
  app: {
    surface: {
      elevated: "#ffffff",
      muted: "#f8fafc",
      selected: "#eef7e7",
    },
    shadow: {
      sm: "0 2px 8px rgba(0,0,0,0.08)",
      md: "0 8px 24px rgba(15, 23, 42, 0.12)",
      lg: "0 18px 45px rgba(15, 23, 42, 0.18)",
    },
    overlay: {
      subtle: "rgba(0,0,0,0.05)",
      medium: "rgba(0,0,0,0.3)",
      strong: "rgba(0,0,0,0.7)",
    },
    audit: {
      normal: "#546e7a",
      importante: "#ef6c00",
      critica: "#c62828",
    },
    cleaning: {
      checkout: "#ea580c",
      intermedia: "#0284c7",
      manual: "#7c3aed",
    },
    // Fuera de servicio: gris pizarra rayado. A propósito no es naranja (el
    // naranja de la fila ya es "limpieza de salida") ni un color de reserva.
    availability: {
      outOfService: "#64748b",
    },
    roles: {
      super_admin: "#546e7a",
      admin: "#1976d2",
      manager: lightWarning,
      limpieza: "#008c91",
    },
  },
});

export const themeDark = createAppTheme("dark", {
  mode: "dark",
  primary: {
    main: "#20c7cd",
    light: "#5ee2e5",
    dark: "#07959a",
  },
  secondary: {
    main: darkActionBlue,
    light: "#9bc6ff",
    dark: "#2f7bd8",
  },
  background: {
    default: "#171a1d",
    paper: "#252b31",
  },
  text: {
    primary: "#f1f5f9",
    secondary: "#c0cad6",
  },
  divider: "rgba(255,255,255,0.14)",
  info: { main: "#64b5f6" },
  success: { main: "#81c784" },
  warning: { main: darkWarning },
  error: { main: "#f87171" },
  zonas: {
    centro: {
      border: "#bbdd55",
      light: "#2f3f1e",
      text: "#d4efb0",
    },
    loma: {
      border: "#33ccd0",
      light: "#004c50",
      text: "#c3f7fa",
    },
    estacionamiento: {
      border: darkWarning,
      light: "#4d3a24",
      text: "#ffe9cc",
    },
    interno: {
      border: "#94a3b8",
      light: "#293341",
      text: "#dbe5f0",
    },
  },
  especial: {
    background: "#e53935",
    text: "#fff",
  },
  app: {
    surface: {
      elevated: "#303842",
      muted: "#20262c",
      selected: "rgba(32,199,205,0.16)",
    },
    shadow: {
      sm: "0 2px 8px rgba(0,0,0,0.22)",
      md: "0 8px 24px rgba(0,0,0,0.3)",
      lg: "0 18px 45px rgba(0,0,0,0.36)",
    },
    overlay: {
      subtle: "rgba(255,255,255,0.06)",
      medium: "rgba(0,0,0,0.42)",
      strong: "rgba(0,0,0,0.76)",
    },
    audit: {
      normal: "#94a3b8",
      importante: darkWarning,
      critica: "#ef9a9a",
    },
    cleaning: {
      checkout: "#fb923c",
      intermedia: "#38bdf8",
      manual: "#a78bfa",
    },
    availability: {
      outOfService: "#94a3b8",
    },
    roles: {
      super_admin: "#cbd5e1",
      admin: darkActionBlue,
      manager: darkWarning,
      limpieza: "#5ee2e5",
    },
  },
});
